import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import { getSupabase } from '@/lib/supabase';
import Logo from '@/components/feature/Logo';

interface AppRow {
  id: string;
  reference_number: string;
  client_full_name: string;
  country_of_travel: string;
  current_stage: number;
  final_decision: string;
  visa_type: string;
  application_type: string;
  submitted_at: string;
}
interface Stats {
  total: number;
  pending: number;
  underReview: number;
  approved: number;
  declined: number;
  countryBreakdown: Record<string, number>;
}

export default function AdminDashboard() {
  const { staffName, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [apps, setApps] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { data, error: fetchErr } = await supabase
        .from('applications')
        .select('id, reference_number, client_full_name, country_of_travel, current_stage, final_decision, visa_type, application_type, submitted_at')
        .order('submitted_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setApps(data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const stats: Stats = {
    total: apps.length,
    pending: apps.filter((a) => a.current_stage < 4 && a.final_decision === 'pending').length,
    underReview: apps.filter((a) => a.current_stage >= 3 && a.current_stage < 6 && a.final_decision === 'pending').length,
    approved: apps.filter((a) => a.final_decision === 'approved').length,
    declined: apps.filter((a) => a.final_decision === 'declined').length,
    countryBreakdown: apps.reduce<Record<string, number>>((acc, a) => {
      acc[a.country_of_travel] = (acc[a.country_of_travel] || 0) + 1;
      return acc;
    }, {}),
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();

  const statCards = [
    { label: 'Total Applications', value: stats.total, icon: 'ri-file-list-3-line', iconColor: 'text-primary-600' },
    { label: 'New / Pending', value: stats.pending, icon: 'ri-hourglass-line', iconColor: 'text-amber-600' },
    { label: 'Under Review', value: stats.underReview, icon: 'ri-file-search-line', iconColor: 'text-accent-600' },
    { label: 'Approved', value: stats.approved, icon: 'ri-check-double-line', iconColor: 'text-green-600' },
    { label: 'Declined', value: stats.declined, icon: 'ri-close-circle-line', iconColor: 'text-red-600' },
  ];

  const getStatusClass = (decision: string, stage: number) => {
    if (decision === 'approved') return 'bg-green-100 text-green-700';
    if (decision === 'declined') return 'bg-red-100 text-red-700';
    if (stage <= 2) return 'bg-secondary-200 text-foreground-600';
    if (stage <= 4) return 'bg-amber-100 text-amber-700';
    if (stage <= 6) return 'bg-primary-100 text-primary-700';
    return 'bg-accent-100 text-accent-700';
  };

  const getStatusLabel = (decision: string, stage: number) => {
    if (decision === 'approved') return 'Approved';
    if (decision === 'declined') return 'Declined';
    const labels: Record<number, string> = { 1: 'Received', 2: 'Docs Collected', 3: 'Under Review', 4: 'Verified', 5: 'Appt Scheduled', 6: 'Submitted', 7: 'Processing', 8: 'Info Requested', 9: 'Decision Made', 10: 'Finalizing', 11: 'Completed' };
    return labels[stage] || 'Pending';
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-primary-900 sticky top-0 z-40">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-3">
              <Logo variant="light" className="w-[130px] md:w-[170px]" />
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-sm md:text-base text-background-50 whitespace-nowrap">PVS Admin</span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-background-50/80 text-sm">
                <div className="w-6 h-6 flex items-center justify-center bg-accent-500 rounded-full">
                  <i className="ri-user-line text-background-50 text-xs"></i>
                </div>
                <span>{staffName}</span>
              </div>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-background-50/10 text-background-50 text-xs font-medium rounded-md border border-background-50/20 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">Logout</button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-background-50 cursor-pointer">
                <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 flex flex-col gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 rounded-md whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 rounded-md whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 rounded-md whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 rounded-md whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
          )}
        </div>
      </header>

      <main className="py-8 md:py-10">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Welcome back, {staffName}</h2>
            <p className="text-sm text-foreground-600 mt-1">Here is your application overview for today.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2">
              <i className="ri-error-warning-line"></i> {error}
              <button onClick={fetchData} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Retry</button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-sm text-foreground-500 mt-3">Loading dashboard...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-10">
                {statCards.map((stat) => (
                  <div key={stat.label} className="bg-background-50 border border-secondary-200 rounded-lg p-4 md:p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-secondary-100 rounded-lg">
                        <i className={`${stat.icon} ${stat.iconColor} text-base md:text-lg`}></i>
                      </div>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
                <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Applications by Country</h3>
                  <div className="space-y-2.5 max-h-[220px] overflow-y-auto">
                    {Object.entries(stats.countryBreakdown).sort(([, a], [, b]) => b - a).slice(0, 8).map(([country, count]) => (
                      <div key={country} className="flex items-center justify-between">
                        <span className="text-sm text-foreground-600">{country}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-secondary-200 rounded-full overflow-hidden">
                            <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(count / (stats.total || 1)) * 100}%` }}></div>
                          </div>
                          <span className="text-xs font-medium text-foreground-900 w-5 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Monthly Summary - {monthNames[currentMonth]} 2026</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Total', value: stats.total, color: 'text-foreground-900' },
                      { label: 'Approved', value: stats.approved, color: 'text-green-600' },
                      { label: 'Declined', value: stats.declined, color: 'text-red-600' },
                      { label: 'Pending', value: stats.pending, color: 'text-amber-600' },
                      { label: 'Under Review', value: stats.underReview, color: 'text-accent-600' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between">
                        <span className="text-sm text-foreground-600">{item.label}</span>
                        <span className={`text-sm font-semibold ${item.color}`}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-secondary-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200">
                  <h3 className="font-heading font-semibold text-base text-foreground-900">Recent Applications</h3>
                  <Link to="/admin/applications" className="text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer">View All</Link>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-200 bg-secondary-50">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Reference</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Client</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Country</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Type</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Status</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Date</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apps.slice(0, 6).map((app) => (
                        <tr key={app.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-primary-700 whitespace-nowrap">{app.reference_number}</td>
                          <td className="px-4 py-3 text-sm text-foreground-800 whitespace-nowrap">{app.client_full_name}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.country_of_travel}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              app.application_type === 'office_appointment' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                            }`}>
                              {app.application_type === 'office_appointment' ? 'Office Visit' : 'Home Collection'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(app.final_decision, app.current_stage)}`}>
                              {getStatusLabel(app.final_decision, app.current_stage)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{app.submitted_at?.split('T')[0]}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <Link to={`/admin/client/${app.id}`} className="inline-block px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer">View</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}